import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { resolveTokenVarRef } from '../../utils/css.js';
import Link from './link.js';
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
/**
 * makeFocusShadow
 *
 * Combines the normal base (or `isFloating` shadow) with a 1px box-shadow imitating a border.
 * The reason for this is that a box-shadow does not alter the dimensions of the DOM object.
 */

var makeFocusShadow = function makeFocusShadow(theme, focusColor, isFloating) {
  var baseShadowVar = resolveTokenVarRef(theme, "shadows.".concat(isFloating ? 'lg' : 'sm'));
  var focusColorVar = resolveTokenVarRef(theme, "colors.".concat(focusColor));
  return "".concat(baseShadowVar, ", 0 0 0 1px ").concat(focusColorVar);
};
var baseStyle = function baseStyle(props) {
  var isFloating = props.isFloating;
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
    touchAction: 'manipulation',
    cursor: 'pointer',
    fontWeight: 600,
    border: 2,
    textStyle: 'text',
    borderRadius: 'base',
    borderStyle: 'solid',
    textAlign: 'center',
    shadow: isFloating ? 'lg' : 'sm',
    transitionProperty: 'common',
    transitionDuration: 'faster',
    _disabled: {
      cursor: 'not-allowed',
      shadow: 'none',
      _loading: {
        shadow: isFloating ? 'lg' : 'sm'
      }
    },
    _hover: {
      // Compatibility layer for bootstrap-based projects when using <Button as="a" /> or <Button as={ReactRouterLink} />
      color: 'white'
    },
    _active: {
      shadow: 'none'
    },
    '.chakra-button__spinner': {
      fontSize: '1.5em',
      '.chakra-spinner': {
        borderWidth: '0.2em'
      }
    }
  };
};
function variantSolid(props) {
  var c = props.colorScheme,
    theme = props.theme,
    isFloating = props.isFloating;
  return {
    bg: "".concat(c, ".400"),
    color: 'white',
    border: 2,
    borderColor: "".concat(c, ".400"),
    borderStyle: 'solid',
    _hover: {
      bg: "".concat(c, ".300"),
      borderColor: "".concat(c, ".300")
    },
    _focus: {
      bg: "".concat(c, ".500"),
      borderColor: "".concat(c, ".600"),
      shadow: makeFocusShadow(theme, "".concat(c, ".600"), isFloating)
    },
    _active: {
      bg: "".concat(c, ".600"),
      borderColor: "".concat(c, ".600")
    },
    _disabled: {
      bg: 'gray.300',
      borderColor: 'gray.300',
      _hover: {
        bg: 'gray.300',
        borderColor: 'gray.300'
      }
    },
    _loading: {
      bg: "".concat(c, ".400"),
      borderColor: "".concat(c, ".400"),
      _hover: {
        bg: "".concat(c, ".400"),
        borderColor: "".concat(c, ".400")
      }
    }
  };
}
function variantInverted(props) {
  var c = props.colorScheme,
    theme = props.theme,
    isFloating = props.isFloating;
  return {
    bg: 'white',
    border: 2,
    color: "".concat(c, ".400"),
    borderColor: 'white',
    _hover: {
      bg: "".concat(c, ".300"),
      color: 'white',
      borderColor: "".concat(c, ".300")
    },
    _focus: {
      bg: 'white',
      borderColor: "".concat(c, ".400"),
      color: "".concat(c, ".400"),
      shadow: makeFocusShadow(theme, "".concat(c, ".400"), isFloating)
    },
    _active: {
      bg: 'gray.100',
      borderColor: 'gray.100',
      color: "".concat(c, ".500")
    },
    _disabled: {
      bg: 'gray.100',
      color: 'gray.400',
      borderColor: 'gray.100',
      _hover: {
        bg: 'gray.100',
        borderColor: 'gray.100',
        color: 'gray.400'
      }
    },
    _loading: {
      bg: 'white',
      borderColor: 'white',
      color: "".concat(c, ".400"),
      _hover: {
        bg: 'white',
        borderColor: 'white',
        color: "".concat(c, ".400")
      }
    }
  };
}
function variantOutline(props) {
  var c = props.colorScheme,
    theme = props.theme,
    isFloating = props.isFloating;
  var baseStyles = variantInverted(props);
  return _objectSpread(_objectSpread({}, baseStyles), {}, {
    borderColor: "".concat(c, ".400"),
    borderWidth: 1,
    _hover: {
      bg: "".concat(c, ".300"),
      color: 'white',
      borderColor: "".concat(c, ".500")
    },
    _focus: {
      shadow: makeFocusShadow(theme, "".concat(c, ".400"), isFloating)
    },
    _active: {
      bg: "gray.100",
      color: "".concat(c, ".500"),
      borderColor: "".concat(c, ".500")
    },
    _loading: _objectSpread(_objectSpread({}, baseStyles._loading), {}, {
      borderColor: "".concat(c, ".400"),
      _hover: _objectSpread(_objectSpread({}, baseStyles._loading._hover), {}, {
        borderColor: "".concat(c, ".400")
      })
    }),
    _disabled: _objectSpread(_objectSpread({}, baseStyles._disabled), {}, {
      borderColor: "gray.400",
      _hover: _objectSpread(_objectSpread({}, baseStyles._disabled._hover), {}, {
        borderColor: "gray.400"
      })
    })
  });
}
function variantGhost(props) {
  var c = props.colorScheme;
  return {
    bg: 'transparent',
    border: 2,
    borderColor: 'transparent',
    shadow: 'none',
    color: "".concat(c, ".400"),
    fontWeight: 400,
    _hover: {
      bg: 'gray.100',
      color: "".concat(c, ".400")
    },
    _focus: {
      borderColor: "".concat(c, ".400"),
      color: "".concat(c, ".400")
    },
    _active: {
      bg: 'gray.300',
      borderColor: 'gray.300',
      color: "".concat(c, ".500")
    },
    _disabled: {
      color: 'gray.400',
      _hover: {
        bg: 'transparent',
        borderColor: 'transparent',
        color: 'gray.400'
      },
      _loading: {
        boxShadow: 'none'
      }
    },
    _loading: {
      color: "".concat(c, ".400"),
      _hover: {
        bg: 'gray.100',
        color: "".concat(c, ".400")
      }
    }
  };
}
function variantLink(props) {
  var baseStyles = Link.baseStyle(props);
  var defaultVariantStyles = Link.variants["default"](props);
  return _objectSpread(_objectSpread(_objectSpread({
    border: 0,
    p: 0,
    shadow: 'none',
    fontWeight: 400
  }, baseStyles), defaultVariantStyles), {}, {
    _disabled: {
      color: 'gray.400',
      _hover: {
        color: 'gray.400'
      }
    },
    _loading: {
      color: defaultVariantStyles.color,
      _hover: defaultVariantStyles.color,
      _disabled: {
        color: defaultVariantStyles.color,
        shadow: 'none'
      }
    }
  });
}
var variantUnstyled = {
  bg: 'none',
  color: 'inherit',
  display: 'inline',
  lineHeight: 'inherit',
  m: 0,
  border: 0,
  p: 0,
  shadow: 'initial',
  _hover: {
    color: 'initial'
  },
  _disabled: {
    _loading: {
      boxShadow: 'none'
    }
  }
};
var variants = {
  solid: variantSolid,
  outline: variantOutline,
  inverted: variantInverted,
  ghost: variantGhost,
  link: variantLink,
  unstyled: variantUnstyled
};
var sizes = {
  sm: {
    py: 2,
    px: 4,
    fontSize: 'md'
  },
  md: {
    px: 4,
    py: 3,
    fontSize: 'lg'
  }
};
var defaultProps = {
  variant: 'solid',
  size: 'md',
  colorScheme: 'blue'
};
var Button = {
  baseStyle: baseStyle,
  variants: variants,
  sizes: sizes,
  defaultProps: defaultProps
};
export { Button as default };